<template>
  <div class="mod-overlay">
    <div class="mod-modal empleado-modal">
      <!-- Header -->
      <div class="mod-modal-header">
        <div class="emp-header-left">
          <h3 class="mod-modal-title">
            <i :class="isEdit ? 'bi bi-pencil' : 'bi bi-plus-circle'"></i>
            {{ isEdit ? 'Editar empleado' : 'Nuevo empleado' }}
          </h3>
          <span v-if="isEdit && form.Nombre" class="emp-header-nombre">{{ form.Nombre }}</span>
        </div>
        <button class="mod-modal-close" @click="$emit('close')">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- Body -->
      <div class="empleado-tabs">
        <button type="button" :class="{ active: activeTab === 'datos' }" @click="activeTab = 'datos'">
          <i class="bi bi-person"></i>
          Datos
        </button>
        <button type="button" :disabled="!isEdit" :class="{ active: activeTab === 'extendido' }" @click="activeTab = 'extendido'">
          <i class="bi bi-shield-check"></i>
          Perfil extendido
        </button>
        <button type="button" :disabled="!isEdit" :class="{ active: activeTab === 'expediente' }" @click="activeTab = 'expediente'">
          <i class="bi bi-folder2-open"></i>
          Expediente
        </button>
      </div>

      <form v-if="activeTab === 'datos'" @submit.prevent="handleSubmit" class="mod-modal-body">
        <!-- === IDENTIFICACIÓN === -->
        <div class="form-section">
          <div class="form-section-hdr">
            <i class="bi bi-person-badge"></i>
            Identificación
          </div>
          <div class="form-g3">
            <div class="mod-field">
              <label for="Clave">Clave <span class="mod-required">*</span></label>
              <input
                type="text"
                id="Clave"
                v-model="form.Clave"
                :disabled="isEdit"
                required
                maxlength="50"
                placeholder="Código del empleado"
              >
            </div>
            <div class="mod-field form-span-2">
              <label for="Nombre">Nombre <span class="mod-required">*</span></label>
              <input
                type="text"
                id="Nombre"
                v-model="form.Nombre"
                required
                placeholder="Nombre completo del empleado"
              >
            </div>
            <div class="mod-field">
              <label for="Clave2">Clave secundaria</label>
              <input
                type="text"
                id="Clave2"
                v-model="form.Clave2"
                placeholder="Clave alternativa"
              >
            </div>
            <div class="mod-field mod-field-check form-check-end">
              <label class="mod-check-label">
                <input type="checkbox" v-model="form.EsActivo">
                <span>Activo</span>
              </label>
            </div>
          </div>
        </div>

        <!-- === SISTEMA / ERP === -->
        <div class="form-section form-section-last">
          <div class="form-section-hdr">
            <i class="bi bi-hdd-network"></i>
            Sistema / ERP
          </div>
          <div class="form-g2">
            <div class="mod-field">
              <label for="UsuarioId">Usuario</label>
              <CatSearch
                id="UsuarioId"
                v-model="form.UsuarioId"
                catalogo="usuario"
                placeholder="Usuario que registrará su asistencia"
              />
              <small class="mod-field-hint">Este usuario podrá registrar entrada y salida desde Mi Perfil.</small>
            </div>
            <div class="mod-field">
              <label for="ClaveErp">Clave ERP</label>
              <input
                type="text"
                id="ClaveErp"
                v-model="form.ClaveErp"
                placeholder="Mapeo con sistema ERP"
              >
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="mod-modal-footer">
          <button type="button" class="mod-btn mod-btn-cancel" @click="$emit('close')">
            Cancelar
          </button>
          <button type="submit" class="mod-btn mod-btn-save" :disabled="saving">
            <span v-if="saving" class="mod-spinner-sm"></span>
            <template v-else>
              <i class="bi bi-check-lg"></i>
              {{ isEdit ? 'Guardar cambios' : 'Crear empleado' }}
            </template>
          </button>
        </div>
      </form>

      <div v-else-if="activeTab === 'extendido'" class="mod-modal-body">
        <EmpleadoExtendidoPanel :empleado-clave="form.Clave" />
      </div>

      <div v-else class="mod-modal-body">
        <EmpleadoArchivosPanel :empleado-clave="form.Clave" />
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import empleadoService from './empleado.service';
import authService from '@/services/auth';
import { createEmpleado } from './empleado.model';
import EmpleadoExtendidoPanel from './EmpleadoExtendidoPanel.vue';
import EmpleadoArchivosPanel from './EmpleadoArchivosPanel.vue';
import CatSearch from '@/shared/components/CatSearch.vue';

export default {
  name: 'EmpleadoForm',
  components: { EmpleadoExtendidoPanel, EmpleadoArchivosPanel, CatSearch },
  props: {
    item: { type: Object, default: null },
    isEdit: { type: Boolean, default: false }
  },
  emits: ['saved', 'close'],
  data() {
    return {
      form: createEmpleado(),
      activeTab: 'datos',
      saving: false
    };
  },
  created() {
    if (this.isEdit && this.item) {
      this.form = { ...this.item };
    } else {
      const empresa = authService.getSelectedEmpresa();
      this.form.EmpresaId = empresa?.EmpresaId || 0;
    }
  },
  methods: {
    async handleSubmit() {
      this.saving = true;

      if (!this.form.Clave || !this.form.Nombre) {
        this.saving = false;
        await Swal.fire({
          icon: 'warning',
          title: 'Campos requeridos',
          text: 'Clave y Nombre son obligatorios',
          confirmButtonColor: '#1272c4'
        });
        return;
      }

      try {
        const result = await empleadoService.guardar(this.form);
        if (result.Number >= 0) {
          await Swal.fire({
            icon: 'success',
            title: 'Guardado',
            text: result.Message || 'Guardado correctamente',
            timer: 2000,
            timerProgressBar: true,
            showConfirmButton: false
          });
          this.$emit('saved');
        } else {
          await Swal.fire({
            icon: 'error',
            title: 'Error al guardar',
            text: result.Message || 'No se pudo guardar el empleado',
            confirmButtonColor: '#1272c4'
          });
        }
      } catch {
        await Swal.fire({
          icon: 'error',
          title: 'Error de conexión',
          text: 'No se pudo conectar con el servidor',
          confirmButtonColor: '#1272c4'
        });
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
@import "@/modules/_shared/catalog.css";

.emp-header-left {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.emp-header-nombre {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  letter-spacing: 0.2px;
  padding-left: 1.75rem;
}

.empleado-modal {
  width: min(1280px, calc(100vw - 32px));
  max-width: min(1280px, calc(100vw - 32px));
  max-height: 90vh;
}

.empleado-tabs {
  display: flex;
  gap: 0.25rem;
  padding: 0.75rem 1.25rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.empleado-tabs button {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.65rem 0.8rem;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #64748b;
  font-weight: 800;
}

.empleado-tabs button.active {
  border-bottom-color: #2563eb;
  color: #2563eb;
}

.empleado-tabs button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Secciones ──────────────────────────────────────── */
.form-section {
  border: 1px solid #e8eef5;
  border-radius: 8px;
  padding: 0.875rem 1rem 1rem;
  background: #ffffff;
  margin-bottom: 0.75rem;
}

.form-section.form-section-last {
  margin-bottom: 0;
}

.form-section-hdr {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.7rem;
  font-weight: 700;
  color: var(--t-primary, #1272c4);
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 0.875rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #edf2f7;
}

.form-section-hdr i {
  font-size: 0.8rem;
}

/* ── Grid layouts ───────────────────────────────────── */
.form-g2,
.form-g3 {
  display: grid;
  gap: 0.75rem 1rem;
}

.form-g2 { grid-template-columns: repeat(2, 1fr); }
.form-g3 { grid-template-columns: repeat(3, 1fr); }

.form-span-2 {
  grid-column: span 2;
}

/* Checkbox alineado al fondo del campo */
.form-check-end {
  align-self: end;
  padding-bottom: 0.45rem;
}

/* ── Responsive ─────────────────────────────────────── */
@media (max-width: 640px) {
  .form-g2,
  .form-g3 {
    grid-template-columns: 1fr;
  }

  .form-span-2 {
    grid-column: span 1;
  }
}
</style>
